import { useCallback, useState } from 'react';
import type { TabKey } from '../addOnTabs';

/**
 * Tracks which Knowledge Hub tabs the visitor has opened, in-memory only (no
 * persistence — resets on reload; see MED-6932).
 *
 * `visited` only ever gains an entry through `markVisited`, called from the
 * `onTabChange` event handler — a tab the user switches AWAY from. The
 * currently active tab doesn't need to be in `visited` to read as "read":
 * `isUnread` treats it as read directly, so the default/opening tab never
 * shows as unread without needing to be seeded.
 *
 * (Deliberately not driven by a `useEffect` keyed on the active tab, nor by a
 * ref compared during render — this project's lint rules disallow synchronous
 * `setState` in an effect and ref reads/writes during render. Updating state
 * from the event that actually changes it avoids both.)
 */
export function useVisitedTabs() {
  const [visited, setVisited] = useState<Set<TabKey>>(() => new Set());

  const markVisited = useCallback((tab: TabKey) => {
    setVisited((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, []);

  const isUnread = useCallback(
    (tab: TabKey, activeTabKey: TabKey | undefined) =>
      tab !== activeTabKey && !visited.has(tab),
    [visited],
  );

  return { markVisited, isUnread };
}
